import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Molecule as OCLMolecule } from 'openchemlib/full';
import { useCallback } from 'react';
import { FaCog, FaCopy, FaDownload, FaFileExport, FaFileImage, FaPaste, FaPlus, FaRegTrashAlt, } from 'react-icons/fa';
import { IoOpenOutline } from 'react-icons/io5';
import { DropdownMenu } from 'react-science/ui';
import { ClipboardFallbackModal } from '../../../utils/clipboard/clipboardComponents';
import { useClipboard } from '../../../utils/clipboard/clipboardHooks';
import { useAssignmentData } from '../../assignment/AssignmentsContext';
import { useDispatch } from '../../context/DispatchContext';
import { useGlobal } from '../../context/GlobalContext';
import ActiveButton from '../../elements/ActiveButton';
import Button from '../../elements/Button';
import { useAlert } from '../../elements/popup/Alert';
import AboutPredictionModal from '../../modal/AboutPredictionModal';
import PredictSpectraModal from '../../modal/PredictSpectraModal';
import { copyPNGToClipboard, exportAsSVG } from '../../utility/export';
import PanelHeader from '../header/PanelHeader';
const styles = {
    counter: {
        margin: 0,
        textAlign: 'right',
        lineHeight: '22px',
        padding: '0 10px',
        whiteSpace: 'nowrap',
    },
    atomLabel: {
        width: '14px',
        height: '14px',
        padding: 0,
        margin: 0,
        textAlign: 'center',
        lineHeight: 1,
    },
};
const LabelWrapper = ({ children }) => {
    return _jsx("p", { style: { padding: '0.4em' }, children: children });
};
const MOL_EXPORT_MENU = [
    {
        type: 'option',
        icon: _jsx(FaCopy, {}),
        label: _jsx(LabelWrapper, { children: "Copy as molfile V3" }),
        data: {
            id: 'molfileV3',
        },
    },
    {
        type: 'option',
        icon: _jsx(FaCopy, {}),
        label: _jsx(LabelWrapper, { children: "Copy as molfile V2" }),
        data: {
            id: 'molfileV2',
        },
    },
    {
        type: 'option',
        icon: _jsx(FaFileImage, {}),
        label: _jsx(LabelWrapper, { children: "Copy as PNG" }),
        data: {
            id: 'png',
        },
    },
    {
        type: 'option',
        icon: _jsx(FaDownload, {}),
        label: _jsx(LabelWrapper, { children: "Export as SVG" }),
        data: {
            id: 'svg',
        },
    },
];
export default function MoleculePanelHeader({ currentIndex, molecules, moleculesView, onMoleculeIndexChange, onOpenMoleculeEditor, renderSource = 'moleculePanel', onClickPreferences, children, }) {
    const { rootRef } = useGlobal();
    const alert = useAlert();
    const dispatch = useDispatch();
    const assignmentData = useAssignmentData();
    const moleculeKey = molecules?.[currentIndex]?.id;
    const saveAsSVGHandler = useCallback(() => {
        if (!rootRef)
            return;
        exportAsSVG(rootRef, `molSVG${currentIndex} `, 'molFile');
    }, [rootRef, currentIndex]);
    const saveAsPNGHandler = useCallback(() => {
        if (!rootRef)
            return;
        copyPNGToClipboard(rootRef, `molSVG${currentIndex} `);
        alert.success('MOL copied as PNG to clipboard');
    }, [rootRef, alert, currentIndex]);
    const { rawWriteWithType, readText, shouldFallback, cleanShouldFallback, text, } = useClipboard();
    const saveAsMolHandler = useCallback((molfile) => {
        void rawWriteWithType(molfile).then(() => {
            alert.success('MOLFile copied to clipboard');
        });
    }, [alert, rawWriteWithType]);
    const exportHandler = useCallback((selected) => {
        const molecule = molecules?.[currentIndex];
        if (molecule) {
            switch (selected?.data.id) {
                case 'molfileV3':
                    saveAsMolHandler(molecule.molfile);
                    break;
                case 'molfileV2': {
                    saveAsMolHandler(OCLMolecule.fromMolfile(molecule.molfile).toMolfile());
                    break;
                }
                case 'png':
                    saveAsPNGHandler();
                    break;
                case 'svg':
                    saveAsSVGHandler();
                    break;
                default:
                    break;
            }
        }
    }, [
        currentIndex,
        molecules,
        saveAsMolHandler,
        saveAsPNGHandler,
        saveAsSVGHandler,
    ]);
    function handlePasteMolfileAction() {
        void readText().then(handlePasteMolfile);
    }
    function handlePasteMolfile(molfile) {
        if (!molfile)
            return;
        dispatch({ type: 'ADD_MOLECULE', payload: { molfile } });
        cleanShouldFallback();
    }
    const handleDelete = useCallback(() => {
        if (molecules[currentIndex]?.id) {
            onMoleculeIndexChange?.(0);
            dispatch({
                type: 'DELETE_MOLECULE',
                payload: { id: molecules[currentIndex].id, assignmentData },
            });
        }
    }, [
        molecules,
        currentIndex,
        onMoleculeIndexChange,
        dispatch,
        assignmentData,
    ]);
    function floatMoleculeHandler() {
        dispatch({
            type: 'FLOAT_MOLECULE_OVER_SPECTRUM',
            payload: { id: moleculeKey },
        });
    }
    function showAtomNumbersHandler() {
        dispatch({
            type: 'TOGGLE_MOLECULE_ATOM_NUMBER',
            payload: { id: moleculeKey },
        });
    }
    const hasMolecules = molecules && molecules.length > 0;
    return (_jsxs(PanelHeader, { children: [renderSource === 'predictionPanel' && _jsx(AboutPredictionModal, {}), renderSource === 'moleculePanel' && (_jsx(DropdownMenu, { trigger: "click", options: MOL_EXPORT_MENU, onSelect: exportHandler, children: _jsx(Button.BarButton, { as: "div", disabled: !hasMolecules, color: { base: '#4e4e4e', hover: '#4e4e4e' }, toolTip: "Export As", tooltipOrientation: "horizontal", children: _jsx(FaFileExport, {}) }) })), _jsx(Button.BarButton, { onClick: handlePasteMolfileAction, color: { base: '#4e4e4e', hover: '#4e4e4e' }, toolTip: "Paste molfile", tooltipOrientation: "horizontal", children: _jsx(FaPaste, {}) }), _jsx(Button.BarButton, { onClick: onOpenMoleculeEditor, color: { base: '#4e4e4e', hover: '#4e4e4e' }, toolTip: "Add molecule", tooltipOrientation: "horizontal", children: _jsx(FaPlus, {}) }), renderSource === 'moleculePanel' && (_jsxs(_Fragment, { children: [_jsx(Button.BarButton, { onClick: handleDelete, color: { base: '#4e4e4e', hover: '#4e4e4e' }, toolTip: "Delete molecule", tooltipOrientation: "horizontal", disabled: !hasMolecules, children: _jsx(FaRegTrashAlt, {}) }), hasMolecules && (_jsx(PredictSpectraModal, { molecule: molecules[currentIndex] }))] })), _jsx(ActiveButton, { value: moleculesView?.[moleculeKey]?.floating.visible || false, popupTitle: "Float molecule", popupPlacement: "left", onClick: floatMoleculeHandler, disabled: !hasMolecules, children: _jsx(IoOpenOutline, {}) }), _jsx(ActiveButton, { style: { marginLeft: '2px' }, value: moleculesView?.[moleculeKey]?.showAtomNumber || false, popupTitle: "Show atom number", popupPlacement: "left", onClick: showAtomNumbersHandler, disabled: !hasMolecules, children: _jsx("p", { style: styles.atomLabel, children: "#" }) }), _jsx("div", { style: { flex: 1 }, children: children }), _jsxs("p", { style: styles.counter, children: [molecules &&
                        molecules.length > 0 &&
                        `${+(currentIndex + 1)} / ${molecules.length}`, ' '] }), onClickPreferences && (_jsx(Button.BarButton, { color: { base: 'black', hover: 'black' }, onClick: onClickPreferences, toolTip: "Preferences", tooltipOrientation: "vertical", children: _jsx(FaCog, {}) })), _jsx(ClipboardFallbackModal, { mode: shouldFallback, onDismiss: cleanShouldFallback, onReadText: handlePasteMolfile, text: text, label: "Molfile" })] }));
}
